import { useRef, useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';

import { useGetAmenities } from '~/hooks/apis/accommodation/useGetAmenities';
import { useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';
import type {
  SaveOfferBuilderRoomParams,
  SavedOfferBuilderRoom,
} from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import { useGenerateBulkBeddingConfigurations } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';
import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import type { FlashOfferProperty } from '../types';
import { alwaysActionable } from '../types';
import { getMissingRequiredFieldLabels } from '../utils';

import { createRooms } from './createRooms';
import { prepareRoomDrafts as prepareRoomDraftsImpl } from './prepareRoomDrafts';
import { buildBlankRoomDraft, getCreatableRoomDrafts } from './utils';
import type { FlashOfferRoomDraft } from './utils';

export interface UseRoomsStepParams {
  isRoomStep: boolean;
  offerId: string;
  saveRoom: (params: SaveOfferBuilderRoomParams) => Promise<SavedOfferBuilderRoom>;
  selectedProperty: FlashOfferProperty | null;
}

/**
 * Owns the Rooms step (3): matching Salesforce deal rooms to existing room types, the manual/
 * AI-assisted room drafts collected from that, and creating them against the selected property.
 */
export function useRoomsStep({ isRoomStep, offerId, saveRoom, selectedProperty }: UseRoomsStepParams) {
  const [roomDrafts, setRoomDrafts] = useState<Array<FlashOfferRoomDraft>>([]);
  const [unmatchedSalesforceRoomNames, setUnmatchedSalesforceRoomNames] = useState<Array<string>>([]);
  const [aiRoomNameOverrides, setAiRoomNameOverrides] = useState<Record<string, string>>({});
  const [salesforceRooms, setSalesforceRooms] = useState<Array<SalesforceRoom>>([]);
  const [additionalAmenityOptions, setAdditionalAmenityOptions] = useState<Array<string>>([]);
  const [isCreatingRooms, setIsCreatingRooms] = useState(false);
  const manualRoomDraftCounter = useRef(0);

  const generateBulkBeddingConfigurations = useGenerateBulkBeddingConfigurations();
  const selectedPropertyId = selectedProperty?.id;
  const {
    data: roomTypesSchema,
    isError: hasRoomTypesSchemaError,
    isFetching: isLoadingRoomTypesSchema,
    refetch: refetchRoomTypesSchema,
  } = useRoomTypesSchema({ propertyId: selectedPropertyId, enabled: isRoomStep && !!selectedPropertyId });
  const { data: amenityOptionsData = [], isFetching: isLoadingAmenities } = useGetAmenities({ enabled: isRoomStep });

  const roomTypesFormSchema = (roomTypesSchema?.post?.body?.schema ?? roomTypesSchema?.put?.body?.schema) as
    | RJSFSchema
    | undefined;
  const amenityOptions = (() => {
    const fetchedAmenityOptions = (amenityOptionsData as Array<{ name?: string | null }>)
      .map((item) => item.name)
      .filter((name): name is string => typeof name === 'string' && !!name.trim());

    return Array.from(new Set<string>(fetchedAmenityOptions.concat(additionalAmenityOptions))).sort();
  })();

  const roomDraftsToCreate = getCreatableRoomDrafts(roomDrafts);
  const missingRequiredRoomFields = roomDraftsToCreate.flatMap((roomDraft, index) =>
    getMissingRequiredFieldLabels(roomTypesFormSchema, roomDraft).map(
      (fieldLabel) => `Room ${index + 1}: ${fieldLabel}`,
    ),
  );
  const hasInvalidRoomDraft = roomTypesFormSchema
    ? missingRequiredRoomFields.length > 0
    : roomDraftsToCreate.some((roomDraft) => !roomDraft.name.trim());
  const isLoadingRoomDraftForm =
    isRoomStep && roomDrafts.length > 0 && (isLoadingRoomTypesSchema || isLoadingAmenities);
  const hasRoomDraftFormError =
    isRoomStep &&
    roomDrafts.length > 0 &&
    !isLoadingRoomTypesSchema &&
    (hasRoomTypesSchemaError || !roomTypesFormSchema);
  const isRoomStepNextDisabled =
    isRoomStep && (isCreatingRooms || isLoadingRoomDraftForm || hasRoomDraftFormError || hasInvalidRoomDraft);

  async function prepareRoomDrafts(property: FlashOfferProperty | null, existingRoomTypes: Array<App.RoomType> = []) {
    const result = await prepareRoomDraftsImpl({
      aiRoomNameOverrides,
      existingRoomTypes,
      generateBulkBeddingConfigurations: async (dealIds) => generateBulkBeddingConfigurations.mutateAsync(dealIds),
      property,
      salesforceRooms,
    });

    if (result.aiRoomNameOverrides) {
      setAiRoomNameOverrides(result.aiRoomNameOverrides);
    }

    setRoomDrafts(result.roomDrafts);
    setUnmatchedSalesforceRoomNames(result.unmatchedSalesforceRoomNames);

    return { roomDrafts: result.roomDrafts, warningMessage: result.warningMessage };
  }

  function updateRoomDraft(draftId: string, updates: Partial<FlashOfferRoomDraft>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => (roomDraft.draftId === draftId ? { ...roomDraft, ...updates } : roomDraft)),
    );
  }

  function handleAddAmenityOption(amenityName: string) {
    const trimmedAmenityName = amenityName.trim();

    if (!trimmedAmenityName) {
      return;
    }

    setAdditionalAmenityOptions((prev) => (prev.includes(trimmedAmenityName) ? prev : prev.concat(trimmedAmenityName)));
  }

  function handleAddRoomDraft(): { errorMessage?: string } {
    if (!selectedPropertyId) {
      return { errorMessage: 'Select or create a property before adding rooms.' };
    }

    manualRoomDraftCounter.current += 1;
    setRoomDrafts((prev) => [
      ...prev,
      buildBlankRoomDraft({
        draftId: `manual-room-${manualRoomDraftCounter.current}`,
        propertyCategory: selectedProperty?.category,
        propertyId: selectedPropertyId,
      }),
    ]);
    return {};
  }

  function handleRemoveRoomDraft(draftId: string) {
    setRoomDrafts((prev) => prev.filter((roomDraft) => roomDraft.draftId !== draftId));
  }

  function markRoomDraftsCreated(savedRoomsByDraftId: Map<string, SavedOfferBuilderRoom>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => {
        const savedRoom = savedRoomsByDraftId.get(roomDraft.draftId);

        if (!savedRoom) {
          return roomDraft;
        }

        return {
          ...roomDraft,
          id: savedRoom.id,
          existingRoomName: savedRoom.name || roomDraft.name,
          shouldCreate: false,
        };
      }),
    );
  }

  async function createSelectedRooms(): Promise<{
    errorMessage?: string;
    savedRoomsByDraftId: Map<string, SavedOfferBuilderRoom>;
    success: boolean;
  }> {
    setIsCreatingRooms(true);

    const { errorMessage, savedRoomsByDraftId, success } = await createRooms({
      hasInvalidRoomDraft,
      hasRoomDraftFormError,
      missingRequiredRoomFields,
      offerId,
      propertyId: selectedPropertyId,
      roomDrafts,
      saveRoom,
    });

    setIsCreatingRooms(false);

    if (savedRoomsByDraftId.size > 0) {
      markRoomDraftsCreated(savedRoomsByDraftId);
    }

    if (!success) {
      return { errorMessage, savedRoomsByDraftId, success: false };
    }

    return { savedRoomsByDraftId, success: true };
  }

  function seedFromOpportunity(nextSalesforceRooms: Array<SalesforceRoom>) {
    setSalesforceRooms(nextSalesforceRooms);
    setRoomDrafts([]);
  }

  // Clears id/existingRoomName/shouldCreate -- existingRoomName here is always the post-creation
  // marker, never a genuine pre-existing match (those never become a draft), so always safe to clear.
  function resetAfterFailedSubmission() {
    setRoomDrafts((prev) =>
      prev.map((draft) =>
        draft.id ? { ...draft, existingRoomName: undefined, id: undefined, shouldCreate: true } : draft,
      ),
    );
  }

  function reset() {
    setAdditionalAmenityOptions([]);
    setSalesforceRooms([]);
    setRoomDrafts([]);
    setUnmatchedSalesforceRoomNames([]);
    setIsCreatingRooms(false);
    manualRoomDraftCounter.current = 0;
  }

  return {
    aiRoomNameOverrides,
    amenityOptions,
    resetAfterFailedSubmission,
    createSelectedRooms,
    handleAddAmenityOption,
    handleAddRoomDraft,
    handleRemoveRoomDraft,
    hasInvalidRoomDraft,
    hasRoomDraftFormError,
    isCreatingRooms,
    isSubmissionFailureActionable: alwaysActionable,
    isLoadingAmenities,
    isLoadingRoomDraftForm,
    isLoadingRoomTypesSchema,
    isRoomStepNextDisabled,
    missingRequiredRoomFields,
    prepareRoomDrafts,
    refetchRoomTypesSchema,
    reset,
    roomDrafts,
    roomDraftsToCreate,
    roomTypesFormSchema,
    salesforceRooms,
    seedFromOpportunity,
    setIsCreatingRooms,
    setRoomDrafts,
    unmatchedSalesforceRoomNames,
    updateRoomDraft,
  };
}
